import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Language } from './landingTranslations';

interface AnimatedStoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  lang: Language;
  onStartFree: () => void;
}

interface StoryScene {
  id: string;
  persona: 'chaos' | 'intro' | 'teacher' | 'parent' | 'principal' | 'finale';
  durationMs: number; // scene playback time in ms
  titleEn: string;
  titleHi: string;
  tagEn: string;
  tagHi: string;
  captionEn: string;
  captionHi: string;
  quoteEn: string;
  quoteHi: string;
  highlightStatEn: string;
  highlightStatHi: string;
}

const SCENES: StoryScene[] = [
  {
    id: 'chaos',
    persona: 'chaos',
    durationMs: 9000,
    tagEn: 'The Old Reality',
    tagHi: 'पुरानी अव्यवस्था',
    titleEn: 'Endless WhatsApp Chaos & Lost Diary Slips',
    titleHi: 'व्हाट्सएप का शोर और गुम होती स्कूल डायरी',
    captionEn: 'Late-night parent pings on teachers\' personal numbers, missed emergency notices, and crumpled notes lost at the bottom of backpacks.',
    captionHi: 'शिक्षकों के निजी नंबर पर देर रात संदेश, छूटे हुए जरूरी स्कूल नोटिस, और बस्ते में मुड़े-तुड़े डायरी के पन्ने।',
    quoteEn: '"Can you share homework again? Did my child reach school?"',
    quoteHi: '"मैम आज का होमवर्क क्या है? क्या मेरा बच्चा स्कूल पहुंचा?"',
    highlightStatEn: '3+ Hours Wasted Daily in Chat Chaos',
    highlightStatHi: 'चैट और फोन कॉल्स में रोज 3+ घंटे बर्बाद'
  },
  {
    id: 'intro',
    persona: 'intro',
    durationMs: 7000,
    tagEn: 'The Solution',
    tagHi: 'समाधान',
    titleEn: 'Meet EduGravity: Calm, Connected Classrooms',
    titleHi: 'मिलिए एड्युग्रेविटी से: शांत, संगठित और पारदर्शी विद्यालय',
    captionEn: 'A purpose-built, zero-clutter workspace uniting Principals, Teachers, and Parents into one joyful, spam-free school ecosystem.',
    captionHi: 'एक ऐसा आधुनिक प्लेटफॉर्म जो प्राचार्यों, शिक्षकों और अभिभावकों को बिना किसी ऐप डाउनलोड या तामझाम के एक साथ जोड़ता है।',
    quoteEn: '"No app store downloads. Works instantly in any mobile browser."',
    quoteHi: '"किसी ऐप डाउनलोड की जरूरत नहीं। हर फोन के ब्राउज़र में तुरंत शुरू।"',
    highlightStatEn: '100% Free For Schools • Zero Ads',
    highlightStatHi: 'स्कूलों के लिए पूर्णतः निःशुल्क • 100% विज्ञापन मुक्त'
  },
  {
    id: 'teacher',
    persona: 'teacher',
    durationMs: 9500,
    tagEn: 'For Teachers',
    tagHi: 'शिक्षकों के लिए',
    titleEn: '10-Second Morning Roll Call & Total Privacy',
    titleHi: '10 सेकंड में हाजिरी और व्यक्तिगत फोन नंबर की 100% सुरक्षा',
    captionEn: 'One tap marks attendance for the entire class. Snap blackboard photos for homework. Communicate with families without ever sharing your private phone number.',
    captionHi: 'एक टैप में पूरी क्लास की हाजिरी। ब्लैकबोर्ड या किताब की फोटो से सेकंडों में होमवर्क भेजें। बिना अपना निजी नंबर दिए अभिभावकों से संवाद करें।',
    quoteEn: '"Roll call takes just 10 seconds. My personal evenings are finally peaceful."',
    quoteHi: '"सुबह की हाजिरी सिर्फ 10 सेकंड में हो जाती है और मेरी शामें अब तनावमुक्त हैं।"',
    highlightStatEn: '1-Tap Attendance • 100% Hidden Phone #',
    highlightStatHi: '1-टैप हाजिरी • फोन नंबर 100% गुप्त और सुरक्षित'
  },
  {
    id: 'parent',
    persona: 'parent',
    durationMs: 9500,
    tagEn: 'For Parents',
    tagHi: 'अभिभावकों के लिए',
    titleEn: 'Real-Time Peace of Mind Every Morning',
    titleHi: 'हर सुबह बच्चे की सुरक्षित उपस्थिति और गृहकार्य की शांति',
    captionEn: 'Know the moment your child safely steps into class. View tonight\'s homework with textbook snapshots and teacher audio notes. Report sick leave with one tap.',
    captionHi: 'बच्चे के क्लास पहुंचते ही उपस्थिति का अलर्ट। किताब की फोटो सहित आज का होमवर्क। बीमारी की छुट्टी की सूचना घर बैठे एक क्लिक में।',
    quoteEn: '"I never have to call other parents asking what today\'s homework was."',
    quoteHi: '"अब दूसरे माता-पिता को फोन करके पूछने की कोई जरूरत नहीं रहती।"',
    highlightStatEn: 'Live Arrival Alerts • Instant Hindi Translation',
    highlightStatHi: 'तुरंत उपस्थिति सूचना • सरल हिन्दी अनुवाद'
  },
  {
    id: 'principal',
    persona: 'principal',
    durationMs: 9000,
    tagEn: 'For Principals',
    tagHi: 'प्राचार्यों के लिए',
    titleEn: 'Effortless Leadership & Spam-Proof Broadcasts',
    titleHi: 'सहज प्रशासनिक नेतृत्व और आपातकालीन कैंपस प्रसारण',
    captionEn: 'Delegate school administration to Vice-Principals in seconds. Send weather alerts or campus updates directly to 500+ parents with zero spam replies.',
    captionHi: 'वाइस-प्रिंसिपल को प्रशासनिक अधिकार तुरंत सौंपें। बारिश या छुट्टी का आपातकालीन नोटिस 500+ परिवारों को 20 सेकंड में भेजें, बिना किसी स्पैम का डर।',
    quoteEn: '"I can broadcast emergency notices to the entire school community in 20 seconds."',
    quoteHi: '"आपातकालीन सूचना पूरे स्कूल के सभी अभिभावकों तक 20 सेकंड में पहुंच जाती है।"',
    highlightStatEn: '1-Click Co-Admin • Zero Unwanted Replies',
    highlightStatHi: '1-क्लिक सह-प्रशासक • शून्य स्पैम और शांति'
  },
  {
    id: 'finale',
    persona: 'finale',
    durationMs: 8000,
    tagEn: 'Ready In 60 Seconds',
    tagHi: 'शुरू करें 60 सेकंड में',
    titleEn: 'Bring Your School Together on EduGravity',
    titleHi: 'अपने पूरे स्कूल को आज ही एड्युग्रेविटी से जोड़ें',
    captionEn: 'Set up your entire school in under 2 minutes. Free forever, private, secure, and delightfully simple for everyone.',
    captionHi: 'केवल 2 मिनट में अपने स्कूल का डिजिटल कैंपस तैयार करें। पूरी तरह सुरक्षित, निजी और सभी के लिए आसान।',
    quoteEn: '"The simplest upgrade your school will make this year."',
    quoteHi: '"आपके स्कूल का इस साल का सबसे सरल और बेहतरीन निर्णय।"',
    highlightStatEn: 'Zero Fees • Zero Setup Delay',
    highlightStatHi: 'शून्य फीस • तुरंत एक्टिवेशन'
  }
];

export const AnimatedStoryModal: React.FC<AnimatedStoryModalProps> = ({
  isOpen,
  onClose,
  lang,
  onStartFree
}) => {
  const [currentSceneIdx, setCurrentSceneIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.85);
  const [bgmEnabled, setBgmEnabled] = useState(true);
  const [speedMultiplier, setSpeedMultiplier] = useState<1 | 1.5>(1);
  const [needsUserTapAudio, setNeedsUserTapAudio] = useState(false);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const masterGainRef = useRef<GainNode | null>(null);
  const bgmGainRef = useRef<GainNode | null>(null);
  const sfxGainRef = useRef<GainNode | null>(null);
  const compressorRef = useRef<DynamicsCompressorNode | null>(null);

  const animationFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);
  const chaosIntervalRef = useRef<number | null>(null);
  const bgmIntervalRef = useRef<number | null>(null);
  const bgmStepRef = useRef(0);

  const currentScene = SCENES[currentSceneIdx];

  // Initialize and get the master Web Audio graph
  const getAudioContext = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioCtx) return null;
        const ctx = new AudioCtx();
        audioCtxRef.current = ctx;

        // Dynamics Compressor (boosts soft sounds, prevents distortion)
        const compressor = ctx.createDynamicsCompressor();
        compressor.threshold.setValueAtTime(-18, ctx.currentTime);
        compressor.knee.setValueAtTime(10, ctx.currentTime);
        compressor.ratio.setValueAtTime(4, ctx.currentTime);
        compressor.attack.setValueAtTime(0.003, ctx.currentTime);
        compressor.release.setValueAtTime(0.2, ctx.currentTime);
        compressor.connect(ctx.destination);
        compressorRef.current = compressor;

        // Master Gain
        const masterGain = ctx.createGain();
        masterGain.gain.setValueAtTime(isMuted ? 0 : volume, ctx.currentTime);
        masterGain.connect(compressor);
        masterGainRef.current = masterGain;

        // SFX Gain
        const sfxGain = ctx.createGain();
        sfxGain.gain.setValueAtTime(1.0, ctx.currentTime);
        sfxGain.connect(masterGain);
        sfxGainRef.current = sfxGain;

        // BGM Gain
        const bgmGain = ctx.createGain();
        bgmGain.gain.setValueAtTime(bgmEnabled ? 0.22 : 0, ctx.currentTime);
        bgmGain.connect(masterGain);
        bgmGainRef.current = bgmGain;
      }

      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume().then(() => {
          setNeedsUserTapAudio(false);
        }).catch(() => {
          setNeedsUserTapAudio(true);
        });
      } else {
        setNeedsUserTapAudio(false);
      }
      return ctx;
    } catch {
      return null;
    }
  }, [isMuted, volume, bgmEnabled]);

  // Update master gains on state change
  useEffect(() => {
    if (masterGainRef.current && audioCtxRef.current) {
      const targetGain = isMuted ? 0 : volume;
      masterGainRef.current.gain.setTargetAtTime(targetGain, audioCtxRef.current.currentTime, 0.05);
    }
  }, [isMuted, volume]);

  useEffect(() => {
    if (bgmGainRef.current && audioCtxRef.current) {
      const targetBgm = (isMuted || !bgmEnabled) ? 0 : 0.22;
      bgmGainRef.current.gain.setTargetAtTime(targetBgm, audioCtxRef.current.currentTime, 0.05);
    }
  }, [bgmEnabled, isMuted]);

  // Background Musical Theme Sequencer (warm marimba chords)
  useEffect(() => {
    if (!isOpen || !isPlaying || isMuted || !bgmEnabled) {
      if (bgmIntervalRef.current) {
        clearInterval(bgmIntervalRef.current);
        bgmIntervalRef.current = null;
      }
      return;
    }

    const chords = [
      // C maj7
      [261.63, 329.63, 392.00, 493.88],
      // G maj
      [196.00, 246.94, 293.66, 392.00],
      // A min7
      [220.00, 261.63, 329.63, 392.00],
      // F maj7
      [174.61, 220.00, 261.63, 329.63]
    ];

    const playBgmStep = () => {
      const ctx = getAudioContext();
      if (!ctx || !bgmGainRef.current || ctx.state !== 'running') return;

      const chordIdx = Math.floor(bgmStepRef.current / 4) % chords.length;
      const noteIdx = bgmStepRef.current % 4;
      const freq = chords[chordIdx][noteIdx];
      bgmStepRef.current = (bgmStepRef.current + 1) % 16;

      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, now);

      gain.gain.setValueAtTime(0.001, now);
      gain.gain.linearRampToValueAtTime(0.18, now + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);

      osc.connect(gain);
      gain.connect(bgmGainRef.current);

      osc.start(now);
      osc.stop(now + 0.48);
    };

    bgmIntervalRef.current = window.setInterval(playBgmStep, 450);

    return () => {
      if (bgmIntervalRef.current) {
        clearInterval(bgmIntervalRef.current);
        bgmIntervalRef.current = null;
      }
    };
  }, [isOpen, isPlaying, isMuted, bgmEnabled, getAudioContext]);

  // High-Definition Sound Effects Synthesizer
  const playSoundEffect = useCallback((type: 'chaos' | 'whoosh' | 'teacher_ticks' | 'parent_bell' | 'principal_chime' | 'celebrate') => {
    if (isMuted) return;
    const ctx = getAudioContext();
    if (!ctx || !sfxGainRef.current) return;
    const targetNode = sfxGainRef.current;

    const now = ctx.currentTime;

    if (type === 'chaos') {
      // 1. Dual Phone Vibration Buzzes ("bzzz... bzzz...")
      [0, 0.22].forEach((offset) => {
        const osc = ctx.createOscillator();
        const filter = ctx.createBiquadFilter();
        const gain = ctx.createGain();

        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(130, now + offset);
        // Add subtle vibration modulation
        osc.frequency.linearRampToValueAtTime(115, now + offset + 0.16);

        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(320, now + offset);

        gain.gain.setValueAtTime(0.35, now + offset);
        gain.gain.linearRampToValueAtTime(0.32, now + offset + 0.12);
        gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.18);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(targetNode);

        osc.start(now + offset);
        osc.stop(now + offset + 0.19);
      });

      // 2. Frantic WhatsApp / Chat Notification Pings ("ding-ding!")
      [0.45, 0.65, 0.85].forEach((offset, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        const freqs = [1760, 2093, 2349.3]; // A6, C7, D7
        osc.frequency.setValueAtTime(freqs[idx % freqs.length], now + offset);

        gain.gain.setValueAtTime(0.28, now + offset);
        gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.22);

        osc.connect(gain);
        gain.connect(targetNode);

        osc.start(now + offset);
        osc.stop(now + offset + 0.23);
      });
    } else if (type === 'whoosh') {
      // Calming Transition Whoosh + Ascending Major Chime (C4 -> G4 -> C5 -> E5 -> G5)
      const freqs = [261.63, 392.00, 523.25, 659.25, 783.99];
      freqs.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + i * 0.08);

        gain.gain.setValueAtTime(0.001, now + i * 0.08);
        gain.gain.linearRampToValueAtTime(0.25, now + i * 0.08 + 0.03);
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.08 + 0.6);

        osc.connect(gain);
        gain.connect(targetNode);

        osc.start(now + i * 0.08);
        osc.stop(now + i * 0.08 + 0.65);
      });
    } else if (type === 'teacher_ticks') {
      // 3 rapid digital roll-call check clicks + checkmark confirmation ding + camera shutter
      [0, 0.08, 0.16].forEach((offset) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(900, now + offset);
        osc.frequency.exponentialRampToValueAtTime(1400, now + offset + 0.04);

        gain.gain.setValueAtTime(0.28, now + offset);
        gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.05);

        osc.connect(gain);
        gain.connect(targetNode);

        osc.start(now + offset);
        osc.stop(now + offset + 0.06);
      });

      // Checkmark confirmation ding (A5)
      const oscDing = ctx.createOscillator();
      const gainDing = ctx.createGain();
      oscDing.type = 'sine';
      oscDing.frequency.setValueAtTime(880, now + 0.28);
      gainDing.gain.setValueAtTime(0.3, now + 0.28);
      gainDing.gain.exponentialRampToValueAtTime(0.001, now + 0.28 + 0.45);
      oscDing.connect(gainDing);
      gainDing.connect(targetNode);
      oscDing.start(now + 0.28);
      oscDing.stop(now + 0.28 + 0.48);

      // Camera shutter snap at 0.75s
      const oscSnap = ctx.createOscillator();
      const gainSnap = ctx.createGain();
      oscSnap.type = 'sawtooth';
      oscSnap.frequency.setValueAtTime(1200, now + 0.75);
      oscSnap.frequency.exponentialRampToValueAtTime(300, now + 0.75 + 0.06);
      gainSnap.gain.setValueAtTime(0.25, now + 0.75);
      gainSnap.gain.exponentialRampToValueAtTime(0.001, now + 0.75 + 0.07);
      oscSnap.connect(gainSnap);
      gainSnap.connect(targetNode);
      oscSnap.start(now + 0.75);
      oscSnap.stop(now + 0.75 + 0.08);
    } else if (type === 'parent_bell') {
      // Warm, reassuring school arrival bell (E5 -> B5 marimba chime)
      const notes = [659.25, 987.77];
      notes.forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + idx * 0.14);

        gain.gain.setValueAtTime(0.32, now + idx * 0.14);
        gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.14 + 0.65);

        osc.connect(gain);
        gain.connect(targetNode);

        osc.start(now + idx * 0.14);
        osc.stop(now + idx * 0.14 + 0.7);
      });
    } else if (type === 'principal_chime') {
      // Classic campus announcement 2-tone chime ("Ding-Dong!" 587.33Hz -> 440Hz / D5 -> A4)
      const chimes = [
        { freq: 587.33, time: 0 },
        { freq: 440.00, time: 0.25 }
      ];
      chimes.forEach(({ freq, time }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + time);

        gain.gain.setValueAtTime(0.35, now + time);
        gain.gain.exponentialRampToValueAtTime(0.001, now + time + 0.75);

        osc.connect(gain);
        gain.connect(targetNode);

        osc.start(now + time);
        osc.stop(now + time + 0.8);
      });
    } else if (type === 'celebrate') {
      // Triumphant Fanfare Arpeggio (C5 -> E5 -> G5 -> C6)
      const fanfare = [523.25, 659.25, 783.99, 1046.50];
      fanfare.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, now + i * 0.1);

        gain.gain.setValueAtTime(0.28, now + i * 0.1);
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.1 + 0.85);

        osc.connect(gain);
        gain.connect(targetNode);

        osc.start(now + i * 0.1);
        osc.stop(now + i * 0.1 + 0.9);
      });
    }
  }, [isMuted, getAudioContext]);

  // Handle Scene Transitions & Sound Triggers
  const goToScene = useCallback((index: number) => {
    const nextIdx = Math.max(0, Math.min(SCENES.length - 1, index));
    setCurrentSceneIdx(nextIdx);
    setProgress(0);
    lastTimeRef.current = null;

    // Clear any previous Chaos buzz interval
    if (chaosIntervalRef.current) {
      clearInterval(chaosIntervalRef.current);
      chaosIntervalRef.current = null;
    }

    if (nextIdx === 0) {
      // Play initial buzz & ping, then repeat every 2.4 seconds while on Chaos
      playSoundEffect('chaos');
      chaosIntervalRef.current = window.setInterval(() => {
        playSoundEffect('chaos');
      }, 2400);
    } else if (nextIdx === 1) {
      playSoundEffect('whoosh');
    } else if (nextIdx === 2) {
      playSoundEffect('teacher_ticks');
    } else if (nextIdx === 3) {
      playSoundEffect('parent_bell');
    } else if (nextIdx === 4) {
      playSoundEffect('principal_chime');
    } else if (nextIdx === 5) {
      playSoundEffect('celebrate');
    }
  }, [playSoundEffect]);

  // Clean up interval when leaving scene or component unmount
  useEffect(() => {
    return () => {
      if (chaosIntervalRef.current) {
        clearInterval(chaosIntervalRef.current);
        chaosIntervalRef.current = null;
      }
    };
  }, []);

  const handleNext = useCallback(() => {
    if (currentSceneIdx < SCENES.length - 1) {
      goToScene(currentSceneIdx + 1);
    } else {
      setIsPlaying(false);
      setProgress(100);
    }
  }, [currentSceneIdx, goToScene]);

  const handlePrev = useCallback(() => {
    if (progress > 20) {
      setProgress(0);
      lastTimeRef.current = null;
    } else if (currentSceneIdx > 0) {
      goToScene(currentSceneIdx - 1);
    }
  }, [currentSceneIdx, progress, goToScene]);

  // Animation playback loop
  useEffect(() => {
    if (!isOpen || !isPlaying) {
      lastTimeRef.current = null;
      return;
    }

    const duration = currentScene.durationMs / speedMultiplier;

    const animate = (time: number) => {
      if (lastTimeRef.current === null) {
        lastTimeRef.current = time;
      }
      const delta = time - lastTimeRef.current;
      lastTimeRef.current = time;

      setProgress(prev => {
        const next = prev + (delta / duration) * 100;
        if (next >= 100) {
          handleNext();
          return 0;
        }
        return next;
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isOpen, isPlaying, currentSceneIdx, currentScene.durationMs, speedMultiplier, handleNext]);

  // Start story and audio when modal opens
  useEffect(() => {
    if (isOpen) {
      goToScene(0);
      setIsPlaying(true);
      // Attempt to immediately start/resume audio on opening
      const ctx = getAudioContext();
      if (ctx && ctx.state === 'suspended') {
        ctx.resume().catch(() => setNeedsUserTapAudio(true));
      }
    } else {
      setIsPlaying(false);
      if (chaosIntervalRef.current) {
        clearInterval(chaosIntervalRef.current);
        chaosIntervalRef.current = null;
      }
      if (bgmIntervalRef.current) {
        clearInterval(bgmIntervalRef.current);
        bgmIntervalRef.current = null;
      }
    }
  }, [isOpen, goToScene, getAudioContext]);

  // Global user interaction resume for audio context
  const handleUserInteraction = useCallback(() => {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().then(() => {
        setNeedsUserTapAudio(false);
      });
    }
  }, [getAudioContext]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      handleUserInteraction();
      if (e.key === 'Escape') onClose();
      if (e.key === ' ' || e.key === 'k') {
        e.preventDefault();
        setIsPlaying(p => !p);
      }
      if (e.key === 'ArrowRight' || e.key === 'l') {
        e.preventDefault();
        handleNext();
      }
      if (e.key === 'ArrowLeft' || e.key === 'j') {
        e.preventDefault();
        handlePrev();
      }
      if (e.key === 'm') {
        setIsMuted(m => !m);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, handleNext, handlePrev, handleUserInteraction]);

  if (!isOpen) return null;

  return (
    <div 
      id="animated-story-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 backdrop-blur-md p-3 sm:p-6 overflow-y-auto animate-fadeIn"
      onClick={(e) => {
        handleUserInteraction();
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div 
        id="animated-story-card"
        className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col my-auto border border-slate-200"
        onClick={handleUserInteraction}
      >
        {/* TOP STATUS & STORY PROGRESS BARS */}
        <div className="bg-slate-900 px-4 sm:px-6 pt-4 pb-3 text-white">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-400 animate-ping"></span>
              <span className="text-xs font-bold uppercase tracking-wider text-slate-300">
                {lang === 'hi' ? 'एड्युग्रेविटी 60-सेकंड स्टोरी' : 'EduGravity 60-Sec Story Tour'}
              </span>

              {/* Live Audio Equalizer indicator */}
              {!isMuted && isPlaying && (
                <div className="hidden sm:flex items-center gap-0.5 ml-2 px-2 py-0.5 rounded-md bg-slate-800 border border-slate-700/60" title="Audio Effects & Music Active">
                  <span className="w-1 h-3 bg-emerald-400 rounded-full animate-bounce [animation-delay:-0.2s]"></span>
                  <span className="w-1 h-4 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.4s]"></span>
                  <span className="w-1 h-2.5 bg-amber-400 rounded-full animate-bounce [animation-delay:-0.1s]"></span>
                  <span className="text-[10px] font-semibold text-slate-300 ml-1">Sound ON</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Background Music Toggle */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUserInteraction();
                  setBgmEnabled(b => !b);
                }}
                className={`p-1.5 px-2 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors ${
                  bgmEnabled && !isMuted 
                    ? 'bg-indigo-900/60 text-indigo-300 border border-indigo-700/50 hover:bg-indigo-800/80' 
                    : 'bg-slate-800 text-slate-400 hover:text-slate-200'
                }`}
                title={bgmEnabled ? 'Music Soundtrack ON (Click to mute music)' : 'Music Soundtrack OFF (Click to enable)'}
              >
                <span className="material-icons-round text-sm">
                  {bgmEnabled ? 'music_note' : 'music_off'}
                </span>
                <span className="hidden md:inline">{bgmEnabled ? 'Music' : 'No Music'}</span>
              </button>

              {/* Speed toggle */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSpeedMultiplier(s => s === 1 ? 1.5 : 1);
                }}
                className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-300 transition-colors"
                title="Playback Speed"
              >
                {speedMultiplier}x
              </button>

              {/* Master Sound toggle */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUserInteraction();
                  setIsMuted(m => !m);
                }}
                className={`p-1.5 px-2.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors ${
                  isMuted 
                    ? 'bg-rose-950/60 border border-rose-800/60 text-rose-300' 
                    : 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                }`}
                title={isMuted ? 'Unmute audio effects & music' : 'Mute all sounds'}
              >
                <span className="material-icons-round text-base">
                  {isMuted ? 'volume_off' : 'volume_up'}
                </span>
                <span className="hidden sm:inline">{isMuted ? 'Unmute' : 'Sound'}</span>
              </button>

              {/* Close Button */}
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg bg-slate-800 hover:bg-rose-900/80 text-slate-300 hover:text-white transition-colors ml-1"
                aria-label="Close"
              >
                <span className="material-icons-round text-lg">close</span>
              </button>
            </div>
          </div>

          {/* Autoplay Unmute Notification if browser paused audio */}
          {needsUserTapAudio && (
            <div 
              onClick={handleUserInteraction}
              className="mb-3 px-3 py-1.5 rounded-xl bg-amber-500/20 border border-amber-400/40 text-amber-200 text-xs flex items-center justify-between cursor-pointer hover:bg-amber-500/30 transition-colors"
            >
              <span className="flex items-center gap-1.5">
                <span className="material-icons-round text-sm text-amber-300">volume_up</span>
                <span>{lang === 'hi' ? 'आवाज़ और संगीत चालू करने के लिए यहाँ टैप करें' : 'Click anywhere to enable voice & sound effects!'}</span>
              </span>
              <span className="text-[11px] underline font-bold">{lang === 'hi' ? 'चालू करें' : 'Tap to Enable'}</span>
            </div>
          )}

          {/* Segmented Story Progress Bar */}
          <div className="grid grid-cols-6 gap-1.5 sm:gap-2">
            {SCENES.map((scene, idx) => {
              let fillWidth = '0%';
              if (idx < currentSceneIdx) fillWidth = '100%';
              else if (idx === currentSceneIdx) fillWidth = `${progress}%`;

              return (
                <button
                  key={scene.id}
                  type="button"
                  onClick={() => goToScene(idx)}
                  className="group relative h-1.5 sm:h-2 bg-slate-800 rounded-full overflow-hidden cursor-pointer hover:h-2.5 transition-all"
                  title={`${scene.titleEn}`}
                >
                  <div 
                    className={`h-full transition-all ease-linear ${
                      idx === 0 ? 'bg-amber-400' :
                      idx === 1 ? 'bg-indigo-400' :
                      idx === 2 ? 'bg-emerald-400' :
                      idx === 3 ? 'bg-blue-400' :
                      idx === 4 ? 'bg-purple-400' :
                      'bg-emerald-400'
                    }`}
                    style={{ width: fillWidth }}
                  />
                </button>
              );
            })}
          </div>

          {/* Scene Persona Pills */}
          <div className="flex items-center justify-between mt-2 pt-1 text-[11px] font-medium text-slate-400 overflow-x-auto gap-2">
            <span className={currentSceneIdx === 0 ? 'text-amber-300 font-bold' : ''}>1. {lang === 'hi' ? 'समस्या' : 'Chaos'}</span>
            <span className={currentSceneIdx === 1 ? 'text-indigo-300 font-bold' : ''}>2. {lang === 'hi' ? 'समाधान' : 'EduGravity'}</span>
            <span className={currentSceneIdx === 2 ? 'text-emerald-300 font-bold' : ''}>3. {lang === 'hi' ? 'शिक्षक' : 'Teachers'}</span>
            <span className={currentSceneIdx === 3 ? 'text-blue-300 font-bold' : ''}>4. {lang === 'hi' ? 'अभिभावक' : 'Parents'}</span>
            <span className={currentSceneIdx === 4 ? 'text-purple-300 font-bold' : ''}>5. {lang === 'hi' ? 'प्राचार्य' : 'Principals'}</span>
            <span className={currentSceneIdx === 5 ? 'text-emerald-300 font-bold' : ''}>6. {lang === 'hi' ? 'शुरू करें' : 'Start'}</span>
          </div>
        </div>

        {/* MAIN VISUAL CANVAS & DOODLE ANIMATIONS */}
        <div className="relative min-h-[340px] sm:min-h-[400px] flex items-center justify-center p-6 sm:p-10 bg-gradient-to-b from-slate-50 via-white to-indigo-50/30 overflow-hidden select-none">
          
          {/* Subtle grid background */}
          <div className="absolute inset-0 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:16px_16px] opacity-60"></div>

          {/* SCENE 1: THE OLD CHAOS */}
          {currentScene.persona === 'chaos' && (
            <div className="relative z-10 flex flex-col items-center text-center max-w-lg animate-fadeIn">
              {/* Animated Doodle: Buzzing Phone with late night alerts & crumpled paper */}
              <div className="relative w-52 h-52 sm:w-60 sm:h-60 mb-4 flex items-center justify-center">
                {/* Buzzing vibration ripples */}
                <div className="absolute inset-0 rounded-full border-2 border-dashed border-amber-300/80 animate-spin [animation-duration:12s]" />
                <div className="absolute w-40 h-40 rounded-full bg-amber-200/40 animate-ping [animation-duration:2.4s]" />
                
                {/* Left & Right Sound Vibration Waves */}
                <div className="absolute -left-2 sm:left-1 flex flex-col gap-1.5 text-amber-500 animate-pulse">
                  <span className="text-base font-extrabold tracking-tighter">(((</span>
                  <span className="text-xs font-bold text-amber-600">BZZZ</span>
                </div>
                <div className="absolute -right-2 sm:right-1 flex flex-col gap-1.5 text-amber-500 animate-pulse [animation-delay:-0.3s]">
                  <span className="text-base font-extrabold tracking-tighter">)))</span>
                  <span className="text-xs font-bold text-amber-600">DING</span>
                </div>

                {/* Center Phone Doodle */}
                <div className="relative z-10 w-28 h-48 bg-slate-900 rounded-3xl p-2.5 shadow-2xl border-4 border-slate-700 rotate-[-6deg] animate-bounce [animation-duration:1.8s]">
                  <div className="w-8 h-1 bg-slate-700 rounded-full mx-auto mb-2" />
                  <div className="space-y-1.5">
                    <div className="bg-rose-500 text-[8px] text-white font-bold p-1 rounded-md shadow-xs animate-pulse flex items-center gap-1">
                      <span className="material-icons-round text-[10px]">notifications_active</span>
                      <span>99+ Unread Messages</span>
                    </div>
                    <div className="bg-amber-100 text-slate-800 text-[8px] p-1 rounded-md font-medium">
                      "Ma'am page 42 homework?"
                    </div>
                    <div className="bg-slate-800 text-slate-400 text-[7px] p-1 rounded-md flex items-center justify-between">
                      <span>WhatsApp Group</span>
                      <span className="text-rose-400 font-mono">10:45 PM</span>
                    </div>
                  </div>
                </div>

                {/* Floating Diary Slip Doodle */}
                <div className="absolute right-0 top-3 bg-yellow-100 border border-yellow-300 p-2.5 rounded-lg shadow-md rotate-[12deg] w-32 text-left">
                  <div className="w-full h-1.5 bg-yellow-300 rounded mb-1" />
                  <p className="text-[9px] font-bold text-amber-900 leading-tight">
                    {lang === 'hi' ? '📝 खोई हुई स्कूल डायरी' : '📝 Lost Diary Slip'}
                  </p>
                  <p className="text-[8px] text-amber-700 line-through">Signed by parent?</p>
                </div>

                {/* Late Clock Badge */}
                <div className="absolute left-0 bottom-4 bg-white border border-slate-200 px-2.5 py-1 rounded-full shadow-sm flex items-center gap-1 text-[10px] font-bold text-slate-600">
                  <span className="material-icons-round text-rose-500 text-xs">alarm</span>
                  <span>10:45 PM</span>
                </div>
              </div>

              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100 text-rose-800 text-xs font-bold mb-2 shadow-2xs">
                <span className="material-icons-round text-sm animate-pulse">volume_up</span>
                <span>{lang === 'hi' ? 'रात 10:45 बजे लगातार बजते व्हाट्सएप संदेश और डायरी का तनाव' : '10:45 PM Constant Phone Buzzing & Chaotic School Groups'}</span>
              </div>
            </div>
          )}

          {/* SCENE 2: THE SPARK (EDUGRAVITY ARRIVAL) */}
          {currentScene.persona === 'intro' && (
            <div className="relative z-10 flex flex-col items-center text-center max-w-lg animate-fadeIn">
              <div className="relative w-48 h-48 sm:w-56 sm:h-56 mb-4 flex items-center justify-center">
                {/* Glowing ring */}
                <div className="absolute inset-0 rounded-full bg-indigo-500/10 animate-ping [animation-duration:3s]" />
                <div className="absolute w-44 h-44 rounded-full border-2 border-indigo-300 border-dashed animate-spin [animation-duration:20s]" />
                
                {/* Center EduGravity Emblem */}
                <div className="relative z-10 w-24 h-24 sm:w-28 sm:h-28 rounded-3xl bg-gradient-to-tr from-indigo-600 to-indigo-500 shadow-xl shadow-indigo-500/30 flex flex-col items-center justify-center text-white">
                  <span className="material-icons-round text-4xl sm:text-5xl">school</span>
                  <span className="text-[10px] font-extrabold uppercase tracking-wider mt-1">EduGravity</span>
                </div>

                {/* 3 Connected Persona Dots */}
                <div className="absolute top-2 left-6 bg-white border border-indigo-200 px-2.5 py-1 rounded-full shadow-sm flex items-center gap-1 text-[10px] font-bold text-indigo-700">
                  <span>👩‍🏫</span> {lang === 'hi' ? 'शिक्षक' : 'Teachers'}
                </div>
                <div className="absolute top-2 right-6 bg-white border border-emerald-200 px-2.5 py-1 rounded-full shadow-sm flex items-center gap-1 text-[10px] font-bold text-emerald-700">
                  <span>👨‍👩‍👧</span> {lang === 'hi' ? 'अभिभावक' : 'Parents'}
                </div>
                <div className="absolute bottom-2 bg-white border border-purple-200 px-2.5 py-1 rounded-full shadow-sm flex items-center gap-1 text-[10px] font-bold text-purple-700">
                  <span>🏛️</span> {lang === 'hi' ? 'प्राचार्य' : 'Principals'}
                </div>
              </div>

              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-xs font-bold mb-2">
                <span className="material-icons-round text-sm">verified</span>
                <span>{lang === 'hi' ? 'स्मार्ट और मानवीय विद्यालय मंच' : 'Purpose-Built School Workspace'}</span>
              </div>
            </div>
          )}

          {/* SCENE 3: FOR TEACHERS (10-SEC ATTENDANCE & PRIVACY) */}
          {currentScene.persona === 'teacher' && (
            <div className="relative z-10 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 max-w-2xl animate-fadeIn">
              {/* Simulated Teacher Phone Mockup */}
              <div className="w-56 sm:w-64 bg-white rounded-3xl shadow-xl border-2 border-slate-200 p-3 space-y-2.5">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-7 h-7 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">5A</span>
                    <div>
                      <p className="text-[10px] font-bold text-slate-800">{lang === 'hi' ? 'कक्षा 5-A हाजिरी' : 'Class 5-A Roll Call'}</p>
                      <p className="text-[8px] text-slate-500">28 Students</p>
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-bold text-[9px] flex items-center gap-0.5">
                    <span className="material-icons-round text-[11px]">done_all</span> 10s
                  </span>
                </div>

                {/* Animated checkmark rows */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between p-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
                    <span className="text-[10px] font-semibold text-slate-800">Aarav Sharma</span>
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] font-bold">✓</span>
                  </div>
                  <div className="flex items-center justify-between p-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
                    <span className="text-[10px] font-semibold text-slate-800">Diya Patel</span>
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] font-bold">✓</span>
                  </div>
                  <div className="flex items-center justify-between p-1.5 rounded-lg bg-slate-50 border border-slate-200">
                    <span className="text-[10px] font-semibold text-slate-800">Rohan Verma</span>
                    <span className="w-5 h-5 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] font-bold">✓</span>
                  </div>
                </div>

                {/* Privacy Badge */}
                <div className="pt-1 border-t border-slate-100 flex items-center gap-1.5 text-[9px] font-bold text-indigo-700 bg-indigo-50/70 p-2 rounded-xl">
                  <span className="material-icons-round text-xs text-indigo-600">lock</span>
                  <span>{lang === 'hi' ? 'टीचर का नंबर 100% सुरक्षित और गुप्त' : 'Teacher Phone # Never Disclosed'}</span>
                </div>
              </div>

              {/* Teacher Story Cards */}
              <div className="space-y-3 max-w-xs text-left">
                <div className="bg-white p-3 rounded-2xl border border-emerald-200 shadow-xs flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                    <span className="material-icons-round text-base">timer</span>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-800">{lang === 'hi' ? '10-सेकंड हाजिरी' : '10-Second Morning Roll'}</h4>
                    <p className="text-[10px] text-slate-500 mt-0.5">{lang === 'hi' ? 'रजिस्टर के पन्ने उलटने की जरूरत नहीं।' : 'No paper registers or manual headcounts.'}</p>
                  </div>
                </div>

                <div className="bg-white p-3 rounded-2xl border border-indigo-200 shadow-xs flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0">
                    <span className="material-icons-round text-base">photo_camera</span>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-800">{lang === 'hi' ? 'किताब की फोटो से होमवर्क' : 'Photo Homework Board'}</h4>
                    <p className="text-[10px] text-slate-500 mt-0.5">{lang === 'hi' ? 'ब्लैकबोर्ड का फोटो खींचकर 1-क्लिक में पोस्ट करें।' : 'Snap a blackboard photo & broadcast in 1 click.'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SCENE 4: FOR PARENTS (PEACE OF MIND & LIVE ARRIVAL) */}
          {currentScene.persona === 'parent' && (
            <div className="relative z-10 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 max-w-2xl animate-fadeIn">
              {/* Parent Phone Notification Simulation */}
              <div className="w-56 sm:w-64 bg-slate-900 rounded-3xl shadow-xl border-4 border-slate-800 p-3 space-y-3 text-white">
                <div className="flex items-center justify-between text-[9px] text-slate-400 px-1">
                  <span>9:41 AM</span>
                  <div className="flex items-center gap-1">
                    <span className="material-icons-round text-[10px]">wifi</span>
                    <span className="material-icons-round text-[10px]">battery_full</span>
                  </div>
                </div>

                {/* Instant Arrival Card */}
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-2.5 border border-white/20 shadow-md">
                  <div className="flex items-center justify-between mb-1">
                    <span className="flex items-center gap-1 text-[9px] font-bold text-emerald-400">
                      <span className="material-icons-round text-xs">notifications_active</span>
                      {lang === 'hi' ? 'उपस्थिति सूचना' : 'EduGravity Alert'}
                    </span>
                    <span className="text-[8px] text-slate-400">8:15 AM</span>
                  </div>
                  <p className="text-[10px] font-bold text-white">
                    {lang === 'hi' ? 'आरव सुरक्षित क्लास में उपस्थित है ✓' : 'Aarav safely marked Present ✓'}
                  </p>
                  <p className="text-[8px] text-slate-300 mt-0.5">Greenwood Academy • Class 5-A</p>
                </div>

                {/* Nightly Homework Card */}
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-2.5 border border-white/20 shadow-md">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[9px] font-bold text-amber-300">📐 {lang === 'hi' ? 'आज का गणित होमवर्क' : 'Maths Assignment'}</span>
                    <span className="text-[8px] bg-amber-400/20 text-amber-200 px-1.5 py-0.5 rounded-full font-bold">Due Tomorrow</span>
                  </div>
                  <p className="text-[9px] text-slate-200">Ex 4.2 Fractions (Q1 - Q6)</p>
                  <div className="mt-1.5 flex items-center justify-between text-[8px] text-indigo-300">
                    <span>📷 Photo Attached</span>
                    <span>🌐 Hindi / English</span>
                  </div>
                </div>
              </div>

              {/* Parent Story Cards */}
              <div className="space-y-3 max-w-xs text-left">
                <div className="bg-white p-3 rounded-2xl border border-blue-200 shadow-xs flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                    <span className="material-icons-round text-base">sentiment_very_satisfied</span>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-800">{lang === 'hi' ? 'सच्ची मानसिक शांति' : 'Complete Peace of Mind'}</h4>
                    <p className="text-[10px] text-slate-500 mt-0.5">{lang === 'hi' ? 'बच्चा स्कूल पहुंचा या नहीं, यह जानने के लिए फोन करने की जरूरत नहीं।' : 'Instant arrival notifications the moment roll call is taken.'}</p>
                  </div>
                </div>

                <div className="bg-white p-3 rounded-2xl border border-amber-200 shadow-xs flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                    <span className="material-icons-round text-base">translate</span>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-800">{lang === 'hi' ? 'सरल भाषा में अनुवाद' : 'No Language Barrier'}</h4>
                    <p className="text-[10px] text-slate-500 mt-0.5">{lang === 'hi' ? 'अंग्रेजी के सभी नोटिस और होमवर्क को 1-टैप में हिन्दी में पढ़ें।' : 'Instant AI translation of all teacher notices.'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SCENE 5: FOR PRINCIPALS (CALM LEADERSHIP & BROADCASTS) */}
          {currentScene.persona === 'principal' && (
            <div className="relative z-10 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10 max-w-2xl animate-fadeIn">
              {/* Emergency Broadcast Dashboard Simulation */}
              <div className="w-56 sm:w-64 bg-white rounded-3xl shadow-xl border-2 border-slate-200 p-3.5 space-y-2.5">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-7 h-7 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center text-sm">🏛️</span>
                    <div>
                      <p className="text-[10px] font-bold text-slate-800">{lang === 'hi' ? 'प्राचार्य कंट्रोल डेस्क' : 'Principal Desk'}</p>
                      <p className="text-[8px] text-slate-500">Greenwood Academy</p>
                    </div>
                  </div>
                  <span className="px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700 font-bold text-[8px]">
                    Co-Admin Active
                  </span>
                </div>

                {/* Campus Broadcast Card */}
                <div className="p-2.5 rounded-2xl bg-rose-50 border border-rose-200 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="px-1.5 py-0.5 rounded-full bg-rose-600 text-white font-bold text-[8px]">
                      URGENT CAMPUS NOTICE
                    </span>
                    <span className="text-[8px] text-rose-700 font-bold">20s ago</span>
                  </div>
                  <p className="text-[10px] font-bold text-slate-800 leading-tight">
                    {lang === 'hi' ? '🌧️ भारी बारिश: स्कूल दोपहर 1 बजे छूटेगा' : '🌧️ Heavy Rain: Early Dismissal at 1:00 PM'}
                  </p>
                  <div className="flex items-center justify-between text-[8px] text-slate-600 pt-1 border-t border-rose-200/60">
                    <span className="font-bold text-emerald-700">✓ 492 / 500 Delivered</span>
                    <span className="bg-slate-200 text-slate-700 px-1 rounded">No Spam Replies</span>
                  </div>
                </div>

                {/* Audit & Compliance */}
                <div className="bg-slate-50 p-2 rounded-xl text-[9px] text-slate-600 flex items-center justify-between">
                  <span>Attendance Rate Today:</span>
                  <span className="font-bold text-indigo-600">96.4%</span>
                </div>
              </div>

              {/* Principal Story Cards */}
              <div className="space-y-3 max-w-xs text-left">
                <div className="bg-white p-3 rounded-2xl border border-purple-200 shadow-xs flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center shrink-0">
                    <span className="material-icons-round text-base">shield</span>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-800">{lang === 'hi' ? '1-क्लिक सह-प्रशासक' : '1-Click Delegation'}</h4>
                    <p className="text-[10px] text-slate-500 mt-0.5">{lang === 'hi' ? 'वाइस-प्रिंसिपल को बिना पासवर्ड शेयर किए तुरंत जिम्मेदारी सौंपें।' : 'Assign Vice-Principals instantly without credential sharing.'}</p>
                  </div>
                </div>

                <div className="bg-white p-3 rounded-2xl border border-rose-200 shadow-xs flex items-start gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
                    <span className="material-icons-round text-base">campaign</span>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-800">{lang === 'hi' ? 'स्पैम-मुक्त आपातकालीन प्रसारण' : 'Spam-Free Broadcasts'}</h4>
                    <p className="text-[10px] text-slate-500 mt-0.5">{lang === 'hi' ? 'ज़रूरी सूचना सभी तक पहुंचेगी, बिना 500 गैर-ज़रूरी संदेशों के।' : 'Emergency notices reach 500+ families with zero reply clutter.'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SCENE 6: THE GRAND FINALE (ALL TOGETHER) */}
          {currentScene.persona === 'finale' && (
            <div className="relative z-10 flex flex-col items-center text-center max-w-lg animate-fadeIn">
              <div className="relative w-36 h-36 sm:w-44 sm:h-44 mb-3 flex items-center justify-center">
                <div className="absolute inset-0 bg-emerald-400/20 rounded-full animate-ping [animation-duration:3s]" />
                
                {/* Community Avatars Circle */}
                <div className="relative z-10 flex items-center -space-x-3">
                  <div className="w-14 h-14 rounded-full bg-indigo-600 text-white border-4 border-white shadow-lg flex items-center justify-center text-xl">
                    👩‍🏫
                  </div>
                  <div className="w-16 h-16 rounded-full bg-emerald-600 text-white border-4 border-white shadow-xl flex items-center justify-center text-2xl z-10">
                    👨‍👩‍👧
                  </div>
                  <div className="w-14 h-14 rounded-full bg-purple-600 text-white border-4 border-white shadow-lg flex items-center justify-center text-xl">
                    🏛️
                  </div>
                </div>
              </div>

              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold mb-3">
                <span className="material-icons-round text-sm">stars</span>
                <span>{lang === 'hi' ? '100% निःशुल्क • 2 मिनट में सक्रिय' : '100% Free • Set Up In 2 Minutes'}</span>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-2 w-full max-w-sm mb-4">
                <div className="bg-white p-2 rounded-xl border border-slate-200 text-center shadow-xs">
                  <p className="text-xs font-extrabold text-indigo-600">₹0</p>
                  <p className="text-[9px] text-slate-500 font-medium">{lang === 'hi' ? 'सदा निःशुल्क' : 'Free Forever'}</p>
                </div>
                <div className="bg-white p-2 rounded-xl border border-slate-200 text-center shadow-xs">
                  <p className="text-xs font-extrabold text-emerald-600">0 MB</p>
                  <p className="text-[9px] text-slate-500 font-medium">{lang === 'hi' ? 'कोई डाउनलोड नहीं' : 'No App Store'}</p>
                </div>
                <div className="bg-white p-2 rounded-xl border border-slate-200 text-center shadow-xs">
                  <p className="text-xs font-extrabold text-purple-600">100%</p>
                  <p className="text-[9px] text-slate-500 font-medium">{lang === 'hi' ? 'डेटा सुरक्षित' : 'Data Private'}</p>
                </div>
              </div>

              {/* Direct Action Button in Story */}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onStartFree();
                }}
                className="w-full sm:w-auto px-8 py-3.5 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-sm shadow-lg shadow-indigo-600/30 flex items-center justify-center gap-2.5 transition-all transform hover:scale-105 cursor-pointer"
              >
                <span>{lang === 'hi' ? 'गूगल से अभी निःशुल्क शुरू करें' : 'Get Started Free with Google'}</span>
                <span className="material-icons-round text-base">arrow_forward</span>
              </button>
            </div>
          )}

          {/* Left / Right Quick Scrub Overlay Arrows */}
          <button
            type="button"
            onClick={handlePrev}
            disabled={currentSceneIdx === 0 && progress < 10}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white/90 hover:bg-white border border-slate-200/80 shadow-md text-slate-700 flex items-center justify-center transition-transform hover:scale-110 disabled:opacity-30 disabled:pointer-events-none cursor-pointer z-20"
            aria-label="Previous scene"
          >
            <span className="material-icons-round text-lg sm:text-xl">chevron_left</span>
          </button>

          <button
            type="button"
            onClick={handleNext}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white/90 hover:bg-white border border-slate-200/80 shadow-md text-slate-700 flex items-center justify-center transition-transform hover:scale-110 cursor-pointer z-20"
            aria-label="Next scene"
          >
            <span className="material-icons-round text-lg sm:text-xl">chevron_right</span>
          </button>
        </div>

        {/* BOTTOM STORY CAPTIONS & PLAY CONTROLS */}
        <div className="bg-white border-t border-slate-200 px-5 sm:px-8 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            
            {/* Scene Headline & Subtitle */}
            <div className="space-y-1 max-w-xl text-left">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-[10px] font-bold uppercase tracking-wider">
                  {lang === 'hi' ? currentScene.tagHi : currentScene.tagEn}
                </span>
                <span className="text-xs font-semibold text-emerald-600">
                  {lang === 'hi' ? currentScene.highlightStatHi : currentScene.highlightStatEn}
                </span>
              </div>
              <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-snug">
                {lang === 'hi' ? currentScene.titleHi : currentScene.titleEn}
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed font-normal">
                {lang === 'hi' ? currentScene.captionHi : currentScene.captionEn}
              </p>
            </div>

            {/* Play/Pause & Replay Controls */}
            <div className="flex items-center gap-2.5 shrink-0 self-end sm:self-center">
              <button
                type="button"
                onClick={() => setIsPlaying(p => !p)}
                className="px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs flex items-center gap-1.5 transition-colors cursor-pointer"
              >
                <span className="material-icons-round text-base">
                  {isPlaying ? 'pause' : 'play_arrow'}
                </span>
                <span>{isPlaying ? (lang === 'hi' ? 'रोकें' : 'Pause') : (lang === 'hi' ? 'चलाएं' : 'Play')}</span>
              </button>

              <button
                type="button"
                onClick={() => goToScene(0)}
                className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs transition-colors cursor-pointer"
                title={lang === 'hi' ? 'दोबारा शुरू से देखें' : 'Restart from beginning'}
              >
                <span className="material-icons-round text-base">replay</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  onClose();
                  onStartFree();
                }}
                className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer"
              >
                <span>{lang === 'hi' ? 'शुरू करें' : 'Start Free'}</span>
                <span className="material-icons-round text-sm">arrow_forward</span>
              </button>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};
